import React, { useState } from 'react';
import { Download, Search, ChevronDown } from 'lucide-react';

export default function StudentPayment() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const invoices = [
    { id: '#INV0025', program: 'Internal Medicine Foundations', created: 'Jul 29 2025', due: 'Dec 29 2025', amount: 800, status: 'paid' },
    { id: '#INV0024', program: 'Emergency Medicine Basics', created: 'Jul 19 2025', due: 'Dec 29 2025', amount: 930, status: 'deposit' },
    { id: '#INV0023', program: 'Introduction to Surgery', created: 'Jul 19 2025', due: 'Dec 29 2025', amount: 650, status: 'paid' },
    { id: '#INV0022', program: 'Internal Medicine Foundations', created: 'Jul 19 2025', due: 'Dec 29 2025', amount: 650, status: 'paid' },
    { id: '#INV0021', program: 'Cardiology for Beginners', created: 'Jul 19 2025', due: 'Dec 29 2025', amount: 650, status: 'paid' },
    { id: '#INV0020', program: 'Dermatology Essentials', created: 'Jul 19 2025', due: 'Dec 29 2025', amount: 650, status: 'paid' },
    { id: '#INV0019', program: 'Medical Imaging & Radiology', created: 'Jul 19 2025', due: 'Dec 29 2025', amount: 650, status: 'paid' },
    { id: '#INV0018', program: 'Medical Research and Ethics', created: 'Jul 19 2025', due: 'Dec 29 2025', amount: 800, status: 'paid' },
    { id: '#INV0017', program: 'Clinical Laboratory Techniques', created: 'Jul 19 2025', due: 'Dec 29 2025', amount: 930, status: 'paid' },
  ];

  const allPayments = 656;
  const succeeded = 568;
  const uncaptured = 0;

  const barChartData = [56, 20, 56, 40, 56, 12, 56, 36, 56, 24, 56, 36];

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.program.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'paid' && invoice.status === 'paid') ||
                         (statusFilter === 'deposit' && invoice.status === 'deposit');
    return matchesSearch && matchesStatus;
  });

  const handleDownload = (invoiceId) => {
    console.log(`Downloading invoice ${invoiceId}`);
  };

  const handleDownloadAll = () => {
    console.log('Downloading all invoices');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-xl font-semibold text-gray-900 mb-5">Payments</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {/* All Payments Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-4 h-4 bg-gray-400 rounded-sm"></div>
              <span className="text-sm font-medium text-gray-700">All Payments</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-4">{allPayments}</div>
            <div className="flex items-end justify-between h-12 gap-1">
              {barChartData.map((height, idx) => (
                <div key={idx} className="flex-1 flex flex-col justify-end h-full">
                  <div 
                    className="w-full bg-[#4AACC5] rounded-sm"
                    style={{ height: `${(height / 56) * 100}%` }}
                  ></div>
                </div>
              ))}
            </div>
          </div>

          {/* Succeeded Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">Succeeded</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-4">{succeeded}</div>
            <div className="h-12">
              <svg className="w-full h-full" viewBox="0 0 240 48" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#93c5fd" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#dbeafe" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
                <path
                  d="M 0,38 C 20,36 40,32 60,28 C 80,24 100,22 120,18 C 140,14 160,10 180,8 C 200,6 220,4 240,2"
                  fill="none"
                  stroke="#60a5fa"
                  strokeWidth="2"
                />
                <path
                  d="M 0,38 C 20,36 40,32 60,28 C 80,24 100,22 120,18 C 140,14 160,10 180,8 C 200,6 220,4 240,2 L 240,48 L 0,48 Z"
                  fill="url(#lineGradient)"
                />
              </svg>
            </div>
          </div>

          {/* Uncaptured Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">Uncaptured</span>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-4">{uncaptured}</div>
            <div className="h-12">
              <svg className="w-full h-full" viewBox="0 0 240 48" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="flatGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#bfdbfe" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#dbeafe" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
                <path
                  d="M 0,24 L 240,24"
                  fill="none"
                  stroke="#93c5fd"
                  strokeWidth="2"
                />
                <path
                  d="M 0,24 L 240,24 L 240,48 L 0,48 Z"
                  fill="url(#flatGradient)"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Filters and Download */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Program Name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-56 h-10 pl-4 pr-10 bg-white rounded border border-gray-300 text-sm focus:outline-none focus:border-[#4AACC5]"
              />
              <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />
            </div>
            
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-44 h-10 px-4 bg-white rounded border border-gray-300 text-sm text-gray-600 appearance-none focus:outline-none focus:border-[#4AACC5] cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="deposit">Deposit Paid</option>
              </select>
              <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          <button
            onClick={handleDownloadAll}
            className="flex items-center gap-2 px-5 py-2 bg-[#4AACC5] hover:bg-[#3d99b0] rounded text-white text-sm font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            Download all
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-blue-50 border-b border-blue-100">
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Invoice ID</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Program Name</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Created Date</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Due Date</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">Amount</th>
                <th className="px-6 py-3 text-center text-sm font-medium text-gray-700">Status</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700">View</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice, idx) => (
                <tr key={invoice.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-3.5 text-sm text-gray-900">{invoice.id}</td>
                  <td className="px-6 py-3.5 text-sm text-gray-900">{invoice.program}</td>
                  <td className="px-6 py-3.5 text-sm text-gray-900">{invoice.created}</td>
                  <td className="px-6 py-3.5 text-sm text-gray-900">{invoice.due}</td>
                  <td className="px-6 py-3.5 text-sm text-gray-900">$ {invoice.amount}</td>
                  <td className="px-6 py-3.5 text-center">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium text-white ${
                      invoice.status === 'paid' ? 'bg-green-600' : 'bg-yellow-500'
                    }`}>
                      {invoice.status === 'paid' ? 'Paid' : 'Deposit Paid'}
                    </span>
                  </td>
                  <td className="px-6 py-3.5">
                    <button
                      onClick={() => handleDownload(invoice.id)}
                      className="text-[#4AACC5] text-sm font-medium hover:text-[#3d99b0] hover:underline"
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}